import React, { useCallback, useState, useEffect, useRef } from "react";
import styles from "./CategoryFilter.module.scss";
import searchFiltersStyles from "../SearchFilters.module.scss";
import CategoryNode from "./CategoryNode";
import { useCategoryCollapse } from "./useCategoryCollapse";
import { getCategoryWithSubcategories, determineSubcategoryToggle } from "./CategoryFilter.utils";
import { scrollToTop } from '../../helpers/Utils';

export interface Category {
    name: string;
    subcategories: string[];
}

export interface CategoryFilterProps {
    filterId: string;
    indexPropertyName: string;
    title: string;
    categories: Category[];
    selectedCategories: string[];
    onCategorySelect: (filterId: string, category: string[]) => void;
    debounceDelay?: number; // Optional: customize debounce delay (default: 300ms)
}

/**
 * CategoryFilter Component
 * 
 * Displays a hierarchical category filter with parent categories and subcategories.
 * Features:
 * - Parent checkbox selects/deselects all subcategories
 * - Partial selection state (check-minus) when some subcategories are selected
 * - Auto-check parent when all subcategories are manually checked
 * - Collapsible subcategory sections with persistent state
 * - Debounced filter updates to improve performance
 */
const CategoryFilter: React.FC<CategoryFilterProps> = ({
    filterId,
    indexPropertyName,
    title,
    categories,
    selectedCategories,
    onCategorySelect,
    debounceDelay = 300,
}) => {
    const { toggleCategory, isCategoryOpen } = useCategoryCollapse();
    const [pendingSelections, setPendingSelections] = useState<string[]>(selectedCategories);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const isUpdatingFromPropRef = useRef(false);
    const isInitialMount = useRef(true);
    const isInitialMountForSync = useRef(true);

    // Debounced update to parent - only sends the final accumulated state
    useEffect(() => {
        // Skip the initial mount
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        // Don't trigger update if we're syncing from props
        if (isUpdatingFromPropRef.current) {
            isUpdatingFromPropRef.current = false;
            return;
        }

        // Clear existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Set new timer to send accumulated changes
        debounceTimerRef.current = setTimeout(() => {
            // Find what changed
            const added = pendingSelections.filter(item => !selectedCategories.includes(item));
            const removed = selectedCategories.filter(item => !pendingSelections.includes(item));
            
            // Only update if there are actual changes
            if (added.length > 0 || removed.length > 0) {
                onCategorySelect(indexPropertyName, [...added, ...removed]);
            }
        }, debounceDelay);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [pendingSelections, filterId, indexPropertyName, onCategorySelect, debounceDelay, selectedCategories]);

    // Sync pending selections with prop changes (from external sources)
    useEffect(() => {
        // Skip the initial mount to avoid setting the flag unnecessarily
        if (isInitialMountForSync.current) {
            isInitialMountForSync.current = false;
            return;
        }
        
        isUpdatingFromPropRef.current = true;
        setPendingSelections(selectedCategories);
    }, [selectedCategories]);

    /**
     * Handle parent category selection/deselection
     * Selects or deselects the parent and all its subcategories
     */
    const handleCategorySelect = useCallback((category: Category) => {
        const categoriesToToggle = getCategoryWithSubcategories(category);
        
        setPendingSelections(prev => {
            const isCurrentlySelected = prev.includes(category.name);
            if (isCurrentlySelected) {
                // Remove all category values
                return prev.filter(item => !categoriesToToggle.includes(item));
            } else {
                // Add all category values
                return [...new Set([...prev, ...categoriesToToggle])];
            }
        });
    }, []);

    /**
     * Handle subcategory selection/deselection
     * Automatically manages parent checkbox state based on subcategory selections
     */
    const handleSubcategorySelect = useCallback((subcategoryValue: string, category: Category) => {
        setPendingSelections(prev => {
            const categoriesToToggle = determineSubcategoryToggle(
                subcategoryValue,
                category,
                prev
            );

            const newSelections = [...prev];
            categoriesToToggle.forEach(item => {
                const index = newSelections.indexOf(item);
                if (index > -1) {
                    newSelections.splice(index, 1);
                } else {
                    newSelections.push(item);
                }
            });
            scrollToTop();
            return newSelections;
        });
    }, []);

    return (
        <div className={`${styles["category-filter"]} ${searchFiltersStyles["filter-item"]}`}>
            <h3 className={searchFiltersStyles["filter-title"]}>{title}</h3>
            {categories.map((category) => (
                <CategoryNode
                    key={category.name}
                    category={category}
                    selectedCategories={pendingSelections}
                    isOpen={isCategoryOpen(category.name)}
                    onCategorySelect={handleCategorySelect}
                    onSubcategorySelect={handleSubcategorySelect}
                    onToggleCollapse={toggleCategory}
                />
            ))}
        </div>
    );
};

export default CategoryFilter;
